class AnnouncementCarousel extends HTMLElement {
  connectedCallback() {
    this.messages = Array.from(this.querySelectorAll('[data-announcement-message]'));
    if (!this.messages.length) return;

    this.interval = Number(this.dataset.interval) || 5000;
    this.currentIndex = 0;
    this.pauseReasons = new Set();
    this.timer = null;
    this.transitionTimer = null;
    this.topbar = document.getElementById('header-topbar');

    this.show(this.currentIndex, true);

    this.addEventListener('mouseenter', this.handleMouseEnter);
    this.addEventListener('mouseleave', this.handleMouseLeave);
    this.addEventListener('focusin', this.handleFocusIn);
    this.addEventListener('focusout', this.handleFocusOut);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    document.addEventListener('shopify:block:select', this.handleBlockSelect);
    document.addEventListener('shopify:block:deselect', this.handleBlockDeselect);

    if (this.topbar) {
      this.topbarObserver = new MutationObserver(this.handleTopbarChange);
      this.topbarObserver.observe(this.topbar, { attributes: true, attributeFilter: ['class'] });
      this.handleTopbarChange();
    }
    this.handleVisibilityChange();

    this.resume();
  }

  disconnectedCallback() {
    this.clearTimer();
    this.clearTransitionTimer();
    this.topbarObserver?.disconnect();
    this.removeEventListener('mouseenter', this.handleMouseEnter);
    this.removeEventListener('mouseleave', this.handleMouseLeave);
    this.removeEventListener('focusin', this.handleFocusIn);
    this.removeEventListener('focusout', this.handleFocusOut);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
  }

  handleMouseEnter = () => this.pause('hover');
  handleMouseLeave = () => this.resume('hover');
  handleFocusIn = () => this.pause('focus');

  handleFocusOut = (event) => {
    if (!this.contains(event.relatedTarget)) this.resume('focus');
  };

  handleVisibilityChange = () => {
    if (document.hidden) this.pause('document-hidden');
    else this.resume('document-hidden');
  };

  handleTopbarChange = () => {
    const unavailable = this.topbar?.classList.contains('header-topbar--hidden') ||
      this.topbar?.classList.contains('header-topbar--open');
    if (unavailable) this.pause('header-unavailable');
    else this.resume('header-unavailable');
  };

  handleBlockSelect = (event) => {
    const message = event.target instanceof Element
      ? event.target.closest('[data-announcement-message]')
      : null;
    if (!message || !this.contains(message)) return;

    const index = this.messages.indexOf(message);
    if (index === -1) return;
    this.pause('theme-editor');
    this.currentIndex = index;
    this.show(index, true);
  };

  handleBlockDeselect = (event) => {
    const message = event.target instanceof Element
      ? event.target.closest('[data-announcement-message]')
      : null;
    if (message && this.contains(message)) this.resume('theme-editor');
  };

  pause(reason) {
    this.pauseReasons.add(reason);
    this.clearTimer();
  }

  resume(reason) {
    if (reason) this.pauseReasons.delete(reason);
    if (this.pauseReasons.size || this.messages.length < 2) return;
    this.clearTimer();
    this.timer = window.setTimeout(() => this.advance(), this.interval);
  }

  clearTimer() {
    if (!this.timer) return;
    window.clearTimeout(this.timer);
    this.timer = null;
  }

  clearTransitionTimer() {
    if (!this.transitionTimer) return;
    window.clearTimeout(this.transitionTimer);
    this.transitionTimer = null;
  }

  advance() {
    this.timer = null;
    if (this.pauseReasons.size) return;
    this.currentIndex = (this.currentIndex + 1) % this.messages.length;
    this.show(this.currentIndex);
    this.resume();
  }

  show(index, instant = false) {
    const incoming = this.messages[index];
    const outgoing = this.messages.find((message) => !message.hidden && message !== incoming);
    if (!incoming) return;
    this.clearTransitionTimer();

    if (instant) {
      this.messages.forEach((message) => {
        message.hidden = message !== incoming;
        message.classList.toggle('announcement-bar__message--active', message === incoming);
      });
      return;
    }

    incoming.hidden = false;
    window.requestAnimationFrame(() => {
      incoming.classList.add('announcement-bar__message--active');
      outgoing?.classList.remove('announcement-bar__message--active');
    });

    if (outgoing) {
      this.transitionTimer = window.setTimeout(() => {
        outgoing.hidden = true;
        this.transitionTimer = null;
      }, 150);
    }
  }
}

if (!customElements.get('announcement-carousel')) {
  customElements.define('announcement-carousel', AnnouncementCarousel);
}

document.addEventListener('DOMContentLoaded', function () {
  const topbar = document.getElementById('header-topbar');
  const overlay = document.getElementById('megamenu-overlay');
  let activeMegamenu = null;
  let activeTrigger = null;
  let megamenuTabHandler = null;

  /**
   * Returns focusable elements within a container, excluding ones hidden via
   * an inactive ancestor (e.g. a non-active megamenu__panel).
   */
  function getFocusableElements(container) {
    return Array.from(
      container.querySelectorAll('a[href], button:not([disabled])')
    ).filter(function (el) { return el.offsetParent !== null; });
  }

  /**
   * Removes the Tab-cycling and outside-focus handlers for a megamenu.
   * Must be called whenever a megamenu is closed or switched away from.
   */
  function cleanupMegamenuHandlers(megamenu) {
    if (megamenuTabHandler) {
      megamenu.removeEventListener('keydown', megamenuTabHandler);
      megamenuTabHandler = null;
    }
    document.removeEventListener('focusin', handleMegamenuFocusIn);
  }

  function handleMegamenuFocusIn(event) {
    if (!activeMegamenu || activeMegamenu.contains(event.target)) return;
    if (event.target === activeTrigger || event.target.matches?.('[data-megamenu-trigger]')) return;
    closeMegamenu(null);
  }

  // Backdrop-last dismissal: called once the menu panel has finished closing.
  function releaseMenuSurface(bodyClass, onReleased) {
    topbar.classList.remove('header-topbar--open');
    overlay.classList.remove('is-active');
    overlay.classList.add('is-fading');
    overlay.addEventListener('transitionend', function onFade(e) {
      if (e.target !== overlay || e.propertyName !== 'opacity') return;
      overlay.removeEventListener('transitionend', onFade);
      if (overlay.classList.contains('is-active')) return;
      overlay.classList.remove('is-fading');
      document.body.classList.remove(bodyClass);
      onReleased?.();
    });
  }

  // ===== Homepage scroll behavior =====
  // Transparent menu over the hero at the very top; the solid white pill
  // hides while scrolling down and reappears only after scrolling up 8 px.
  if (topbar && topbar.dataset.home === 'true') {
    const TOP_THRESHOLD = 8;
    let lastScrollY = window.scrollY;
    let isHidden = false;
    let scrollUpStartY = null;
    let ticking = false;

    function updateHeaderOnScroll() {
      // DialogComponent locks scroll with body { position: fixed }, which
      // resets window.scrollY to 0. Ignore that synthetic jump.
      if (document.body.style.position === 'fixed') {
        ticking = false;
        return;
      }

      // Re-query: the header section can be re-rendered (theme editor / theme dev
      // hot reload), which would leave the outer reference pointing at a detached node.
      const topbar = document.getElementById('header-topbar');
      if (!topbar) {
        ticking = false;
        return;
      }

      const currentY = window.scrollY;
      const menuOpen = topbar.classList.contains('header-topbar--open');

      if (currentY <= TOP_THRESHOLD) {
        // At the top: show the transparent menu over the hero.
        topbar.classList.add('header-topbar--transparent');
        topbar.classList.remove('header-topbar--hidden');
        isHidden = false;
        scrollUpStartY = null;
      } else if (topbar.dataset.sticky === 'true') {
        // Merchant setting: keep the menu visible regardless of scroll direction.
        topbar.classList.remove('header-topbar--transparent');
        topbar.classList.remove('header-topbar--hidden');
        isHidden = false;
        scrollUpStartY = null;
      } else {
        const scrollingDown = currentY > lastScrollY;
        const scrollingUp = currentY < lastScrollY;

        if (scrollingDown && !menuOpen) {
          // Scrolling down: slide the bar away while keeping its current look,
          // so the transparent→solid change is never visible on screen.
          scrollUpStartY = null;
          isHidden = true;
          topbar.classList.add('header-topbar--hidden');
        } else if (currentY < lastScrollY && isHidden) {
          if (scrollUpStartY === null) scrollUpStartY = lastScrollY;
          if (scrollUpStartY - currentY >= TOP_THRESHOLD) {
            topbar.classList.remove('header-topbar--transparent');
            topbar.classList.remove('header-topbar--hidden');
            isHidden = false;
            scrollUpStartY = null;
          }
        }
      }

      lastScrollY = currentY;
      ticking = false;
    }

    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          window.requestAnimationFrame(updateHeaderOnScroll);
          ticking = true;
        }
      },
      { passive: true }
    );

    // Set the correct initial state (e.g. when loading already scrolled down).
    updateHeaderOnScroll();
  } else if (topbar) {
    // ===== Non-homepage scroll behavior =====
    // The solid pill-white menu hides while scrolling down and reappears
    // only after scrolling up 8 px. No transparency involved.
    let lastScrollY = window.scrollY;
    let isHidden = false;
    let scrollUpStartY = null;
    let ticking = false;
    const TOP_THRESHOLD = 8;

    function updateHeaderOnScroll() {
      // DialogComponent locks scroll with body { position: fixed }, which
      // resets window.scrollY to 0. Ignore that synthetic jump.
      if (document.body.style.position === 'fixed') {
        ticking = false;
        return;
      }

      const topbar = document.getElementById('header-topbar');
      if (!topbar) {
        ticking = false;
        return;
      }

      const currentY = window.scrollY;

      // Read the live setting: the theme editor can swap the header in place.
      if (topbar.dataset.sticky === 'true') {
        topbar.classList.remove('header-topbar--hidden');
        isHidden = false;
        scrollUpStartY = null;
        lastScrollY = currentY;
        ticking = false;
        return;
      }

      const menuOpen = topbar.classList.contains('header-topbar--open');
      const scrollingDown = currentY > lastScrollY;
      const scrollingUp = currentY < lastScrollY;

      if (scrollingDown && !menuOpen && currentY > TOP_THRESHOLD) {
        scrollUpStartY = null;
        isHidden = true;
        topbar.classList.add('header-topbar--hidden');
      } else if (currentY < lastScrollY && isHidden) {
        if (scrollUpStartY === null) scrollUpStartY = lastScrollY;
        if (scrollUpStartY - currentY >= TOP_THRESHOLD) {
          topbar.classList.remove('header-topbar--hidden');
          isHidden = false;
          scrollUpStartY = null;
        }
      }

      lastScrollY = currentY;
      ticking = false;
    }

    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          window.requestAnimationFrame(updateHeaderOnScroll);
          ticking = true;
        }
      },
      { passive: true }
    );
  }

  function openMegamenu(trigger) {
    const handle = trigger.dataset.megamenuTrigger;
    const megamenu = document.getElementById('megamenu-' + handle);
    if (!megamenu) return;

    const isSwitching = activeMegamenu && activeMegamenu !== megamenu;

    if (isSwitching) {
      // Snap old menu away instantly — no close animation when switching
      const old = activeMegamenu;
      cleanupMegamenuHandlers(old);
      old.classList.remove('is-open', 'is-switching');
      old.setAttribute('aria-hidden', 'true');
      activeTrigger.setAttribute('aria-expanded', 'false');
      resetMegamenu(old);
    }

    activeMegamenu = megamenu;
    activeTrigger = trigger;

    trigger.setAttribute('aria-expanded', 'true');
    megamenu.setAttribute('aria-hidden', 'false');
    topbar.classList.add('header-topbar--open');
    overlay.classList.remove('is-fading');
    overlay.classList.add('is-active');
    document.body.classList.add('megamenu-open');

    // Ensure clean state before adding classes
    megamenu.classList.remove('is-closing', 'is-switching');
    if (isSwitching) {
      megamenu.classList.add('is-switching');
    }
    megamenu.classList.add('is-open');
    trackMegamenuPanel(megamenu, trigger);

    // Tab-cycle handler:
    //   Tab on last item     → close megamenu, focus next header nav item
    //   Shift+Tab on first   → close megamenu, focus the trigger
    megamenuTabHandler = function (e) {
      if (e.key !== 'Tab') return;
      const focusable = getFocusableElements(megamenu);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        // Focusable items scoped to nav + utility only (excludes logo and mobile toggle)
        const navFocusable = Array.from(
          topbar.querySelectorAll(
            '.header-nav a[href], .header-nav button:not([disabled]),' +
            ' .header-utility a[href], .header-utility button:not([disabled])'
          )
        );
        const idx = navFocusable.indexOf(trigger);
        const next = idx !== -1 ? (navFocusable[idx + 1] || null) : null;
        closeMegamenu(next); // null → focus goes to first element after header
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        closeMegamenu(trigger);
      }
    };
    megamenu.addEventListener('keydown', megamenuTabHandler);

    // Close only when focus actually moves outside (Safari may leave it on body during a click).
    document.addEventListener('focusin', handleMegamenuFocusIn);

    // Move focus to the first focusable element so keyboard and screen-reader
    // users can immediately interact with the megamenu
    const firstFocusable = getFocusableElements(megamenu)[0];
    if (firstFocusable) firstFocusable.focus();
  }

  function trackMegamenuPanel(megamenu, trigger) {
    const panel = megamenu.querySelector('.megamenu__panel--active');
    if (!panel || !panel.querySelector('.megamenu__product-card[data-product-id]')) return;

    const menuName = trigger?.textContent?.trim() || 'Shop';
    const panelName = megamenu.querySelector('.megamenu__sidebar-item--active')?.textContent?.trim();
    document.dispatchEvent(
      new CustomEvent('tracking:item-list-view', {
        detail: {
          root: panel,
          itemListId: `shop_menu_${panel.id}`,
          itemListName: panelName ? `${menuName}: ${panelName}` : menuName,
        },
      })
    );
  }

  function resetMegamenu(megamenu) {
    // Reset product-panel megamenu (sidebar buttons)
    const firstItem = megamenu.querySelector('.megamenu__sidebar-item');
    if (firstItem) {
      megamenu.querySelectorAll('.megamenu__sidebar-item').forEach(function (i) {
        i.classList.remove('megamenu__sidebar-item--active');
      });
      firstItem.classList.add('megamenu__sidebar-item--active');

      megamenu.querySelectorAll('.megamenu__panel').forEach(function (p) {
        p.classList.remove('megamenu__panel--active');
      });
      const panelId = firstItem.dataset.panelTarget;
      const panel = document.getElementById(panelId);
      if (panel) panel.classList.add('megamenu__panel--active');

      megamenu.querySelectorAll('.megamenu__footer-btn').forEach(function (b) {
        b.classList.remove('megamenu__footer-btn--active');
      });
      const footerId = firstItem.dataset.footerTarget;
      const footerBtn = document.getElementById(footerId);
      if (footerBtn) footerBtn.classList.add('megamenu__footer-btn--active');
    }

  }

  /**
   * Closes the active megamenu.
   *
   * @param {Element|null|undefined} focusTarget
   *   - Element  → focus that element after closing
   *   - undefined (default) → focus the trigger if focus was inside the megamenu
   *   - null → do not move focus (focus already moved by the caller)
   */
  function closeMegamenu(focusTarget) {
    if (!activeMegamenu) return;

    const megamenuToClose = activeMegamenu;
    const triggerToClose = activeTrigger;

    activeMegamenu = null;
    activeTrigger = null;

    // Remove Tab-cycling and focusout handlers before anything else
    cleanupMegamenuHandlers(megamenuToClose);

    triggerToClose.setAttribute('aria-expanded', 'false');

    // Focus stays with the menu until the backdrop has faded
    const focusAfterClose =
      focusTarget !== undefined
        ? focusTarget
        : megamenuToClose.contains(document.activeElement)
        ? triggerToClose
        : null;

    megamenuToClose.classList.remove('is-open', 'is-switching');
    megamenuToClose.classList.add('is-closing');

    megamenuToClose.addEventListener('animationend', function onClose(e) {
      if (e.target !== megamenuToClose) return;
      megamenuToClose.removeEventListener('animationend', onClose);
      finishMegamenuClose(megamenuToClose, focusAfterClose);
    });

    // No slide-out runs when the panel isn't displayed (e.g. viewport resized to mobile)
    if (getComputedStyle(megamenuToClose).display === 'none') {
      finishMegamenuClose(megamenuToClose, focusAfterClose);
    }
  }

  function finishMegamenuClose(megamenu, focusAfterClose) {
    // Reopened before the close finished
    if (!megamenu.classList.contains('is-closing')) return;
    megamenu.classList.remove('is-closing');
    megamenu.setAttribute('aria-hidden', 'true');
    resetMegamenu(megamenu);
    if (!activeMegamenu) {
      releaseMenuSurface('megamenu-open', function () {
        if (focusAfterClose) focusAfterClose.focus();
      });
    }
  }

  // Nav trigger buttons
  document.querySelectorAll('[data-megamenu-trigger]').forEach(function (trigger) {
    trigger.addEventListener('click', function (e) {
      e.stopPropagation();
      if (trigger.getAttribute('aria-expanded') === 'true') {
        closeMegamenu();
      } else {
        openMegamenu(trigger);
      }
    });
  });

  // Sidebar panel switching
  document.querySelectorAll('.megamenu__sidebar-item').forEach(function (item) {
    item.addEventListener('click', function () {
      const megamenu = item.closest('.megamenu');
      const panelId = item.dataset.panelTarget;
      const footerId = item.dataset.footerTarget;

      megamenu.querySelectorAll('.megamenu__sidebar-item').forEach(function (i) {
        i.classList.remove('megamenu__sidebar-item--active');
      });
      item.classList.add('megamenu__sidebar-item--active');

      megamenu.querySelectorAll('.megamenu__panel').forEach(function (p) {
        p.classList.remove('megamenu__panel--active');
      });
      const panel = document.getElementById(panelId);
      if (panel) panel.classList.add('megamenu__panel--active');

      megamenu.querySelectorAll('.megamenu__footer-btn').forEach(function (b) {
        b.classList.remove('megamenu__footer-btn--active');
      });
      const footerBtn = document.getElementById(footerId);
      if (footerBtn) footerBtn.classList.add('megamenu__footer-btn--active');

      const firstProduct = panel?.querySelector('.megamenu__product-card[href]');
      if (firstProduct) firstProduct.focus();

      trackMegamenuPanel(megamenu, activeTrigger);
    });
  });

  // Close on overlay click
  overlay.addEventListener('click', function () { closeMegamenu(); });

  // Close on Escape key
  document.addEventListener('keydown', function (e) {
    if (e.defaultPrevented || e.key !== 'Escape') return;
    closeMegamenu();
  });

  // ===== Mobile menu =====
  const mobileToggle = document.getElementById('header-mobile-toggle');
  const mobileMenu = document.getElementById('header-mobile-menu');

  if (mobileToggle && mobileMenu) {
    let activeSubmenu = null;
    let submenuTabHandler = null;
    const rootPanel = mobileMenu.querySelector('.header-mobile-menu__panel--root');

    /**
     * Closes the active submenu and moves focus to the given target element.
     * If focusTarget is undefined (default), focus returns to the trigger that
     * opened the submenu. Pass null to skip focusing entirely.
     */
    function resetSubmenus(focusTarget) {
      if (!activeSubmenu) return;
      const submenu = activeSubmenu;
      const trigger = mobileMenu.querySelector(
        '[data-submenu-open="' + submenu.id + '"]'
      );
      submenu.classList.remove('is-active');
      submenu.setAttribute('aria-hidden', 'true');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      // Remove the Tab-cycling handler attached when the submenu opened
      if (submenuTabHandler) {
        submenu.removeEventListener('keydown', submenuTabHandler);
        submenuTabHandler = null;
      }
      activeSubmenu = null;
      // Restore keyboard access to the root panel
      if (rootPanel) rootPanel.removeAttribute('inert');
      // Determine where focus should land:
      //   - explicit target passed by caller  → use it
      //   - undefined (default)               → return to trigger
      //   - only while the mobile menu is still open
      const target = focusTarget !== undefined ? focusTarget : trigger;
      if (target && mobileMenu.classList.contains('is-open')) target.focus();
    }

    function openMobileMenu() {
      rootPanel?.querySelector('.header-mobile-menu__list')?.scrollTo(0, 0);
      mobileMenu.classList.remove('is-closing');
      mobileMenu.classList.add('is-open');
      mobileMenu.setAttribute('aria-hidden', 'false');
      mobileToggle.setAttribute('aria-expanded', 'true');
      topbar.classList.add('header-topbar--open');
      overlay.classList.remove('is-fading');
      overlay.classList.add('is-active');
      document.body.classList.add('mobile-menu-open');
    }

    function closeMobileMenu() {
      if (!mobileMenu.classList.contains('is-open')) return;
      mobileMenu.classList.remove('is-open');
      mobileMenu.classList.add('is-closing');
      mobileToggle.setAttribute('aria-expanded', 'false');

      // Focus stays with the menu until the backdrop has faded
      const focusAfterClose = mobileMenu.contains(document.activeElement) ? mobileToggle : null;

      mobileMenu.addEventListener('animationend', function onClose(e) {
        // Only react to the panel's own slide-out, not the content fade
        if (e.target !== mobileMenu) return;
        mobileMenu.removeEventListener('animationend', onClose);
        finishMobileClose(focusAfterClose);
      });

      // No slide-out runs when the panel isn't displayed (e.g. closed by resizing to desktop)
      if (getComputedStyle(mobileMenu).display === 'none') finishMobileClose(focusAfterClose);
    }

    function finishMobileClose(focusAfterClose) {
      if (!mobileMenu.classList.contains('is-closing')) return;
      mobileMenu.classList.remove('is-closing');
      mobileMenu.setAttribute('aria-hidden', 'true');
      resetSubmenus();
      releaseMenuSurface('mobile-menu-open', function () {
        if (focusAfterClose) focusAfterClose.focus();
      });
    }

    mobileToggle.addEventListener('click', function (e) {
      e.stopPropagation();
      if (mobileMenu.classList.contains('is-open')) {
        closeMobileMenu();
      } else {
        openMobileMenu();
      }
    });

    // Open a sub level
    mobileMenu.querySelectorAll('[data-submenu-open]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        const submenu = document.getElementById(btn.dataset.submenuOpen);
        if (!submenu) return;
        resetSubmenus();
        submenu.classList.add('is-active');
        submenu.setAttribute('aria-hidden', 'false');
        btn.setAttribute('aria-expanded', 'true');
        activeSubmenu = submenu;
        // Prevent root panel items from being reachable while submenu is visible
        if (rootPanel) rootPanel.setAttribute('inert', '');

        // Tab-cycle handler:
        //   Tab on last item     → close submenu, focus next root item (or toggle)
        //   Shift+Tab on first   → close submenu, focus the trigger that opened it
        submenuTabHandler = function (e) {
          if (e.key !== 'Tab') return;
          const focusable = Array.from(
            submenu.querySelectorAll('button:not([disabled]), a[href]')
          );
          if (!focusable.length) return;
          const first = focusable[0];
          const last = focusable[focusable.length - 1];

          if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            // Find the root-panel item that comes after the trigger
            const rootFocusable = Array.from(
              mobileMenu.querySelectorAll(
                '.header-mobile-menu__panel--root button:not([disabled]), ' +
                '.header-mobile-menu__panel--root a[href]'
              )
            );
            const idx = rootFocusable.indexOf(btn);
            const next = rootFocusable[idx + 1] || null;
            resetSubmenus(next !== null ? next : mobileToggle);
          } else if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            resetSubmenus(btn);
          }
        };
        submenu.addEventListener('keydown', submenuTabHandler);

        // Move focus to the first focusable element in the submenu (back button)
        const firstFocusable = submenu.querySelector('button, a[href]');
        if (firstFocusable) firstFocusable.focus({ preventScroll: true });
      });
    });

    // Back to the root level
    mobileMenu.querySelectorAll('[data-submenu-close]').forEach(function (btn) {
      btn.addEventListener('click', function () { resetSubmenus(); });
    });

    // Close on overlay click (shared backdrop)
    overlay.addEventListener('click', closeMobileMenu);

    // Close on Escape
    document.addEventListener('keydown', function (e) {
      if (e.defaultPrevented || e.key !== 'Escape') return;
      closeMobileMenu();
    });

    // Close when leaving the mobile viewport
    window.addEventListener('resize', function () {
      if (window.innerWidth >= 850) closeMobileMenu();
    });
  }
});